import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoreVertical, Save, FolderOpen, CheckCircle } from 'lucide-react';
import { generateTrainingJSON, getTrainingData, iRawTrainingData, saveTrainingData } from './trainingDataConverter';
import TextAreaTrainingData from './textAreaTrainingData';



export function TrainingDataPage() {
  const [trainingData, setTrainingData] = useState<iRawTrainingData[]>([]);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{ message1: string; message2: string; message3: string }>({ message1: '', message2: '', message3: '' });
  const [showRowDataModal, setShowRowDataModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<iRawTrainingData | null>(null);

  const handleStartEdit = useCallback((item: iRawTrainingData) => {
    setEditingRowId(item.rowId);
    setEditValues({ message1: item.messages[0]?.content || '', message2: item.messages[1]?.content || '', message3: item.messages[2]?.content || '' });
  }, []);

  useEffect(() => {
    async function retrieveTrainingData() {
      try {
        const initialData: iRawTrainingData[] = await getTrainingData();
        setTrainingData(initialData);
      } catch (error) {
        console.error("Error retrieving training data:", error);
      }
    }
    retrieveTrainingData();
  }, []);

  const handleSaveChanges = () => {
    if (!editingRowId) return;
    setTrainingData(prev => prev.map(row => {
      if (row.rowId === editingRowId) {
        const updatedMessages = [...row.messages];
        updatedMessages[0] = { ...updatedMessages[0], content: editValues.message1 };
        updatedMessages[1] = { ...updatedMessages[1], content: editValues.message2 };
        updatedMessages[2] = { ...updatedMessages[2], content: editValues.message3 };
        return { ...row, messages: updatedMessages };
      } else {
        return row;
      }
    }
    ));
    setEditingRowId(null);
    setEditValues({ message1: '', message2: '', message3: '' });
  };

  const handleDiscardChanges = () => {
    setEditingRowId(null);
    setEditValues({ message1: '', message2: '', message3: '' });
  };

  const handleDelete = (id: number) => {
    // setData(prev => prev.filter(item => item.id !== id));
  };

  const handleShowRowData = (item: iRawTrainingData) => {
    setSelectedRowData(item);
    setShowRowDataModal(true);
  };

  const handleGenerateJSON = () => {
      generateTrainingJSON();
  }

  const handleSaveFile = () => {
    saveTrainingData(trainingData).then(() => {
      alert('Training data saved successfully!');
    }).catch((error) => {
      console.error('Error saving training data:', error);
      alert('Failed to save training data.');
    });
  };

  const handleOpenFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const parsed = JSON.parse(event.target?.result as string);
            if (Array.isArray(parsed)) {
              setTrainingData(parsed);
            }
          } catch {
            console.error('Invalid JSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleValidateFile = () => {
    // const isValid = trainingData.every(item =>
    //   item.rowId && typeof item.question === 'string' && typeof item.answer === 'string'
    // );
    // alert(isValid ? 'Data is valid!' : 'Data validation failed!');
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold text-foreground">Training Data</h1>

      {/* Top Action Buttons */}
      <div className="flex gap-4">
        <Button onClick={handleSaveFile} className="gap-2">
          <Save className="h-4 w-4" />
          Save File
        </Button>
        <Button onClick={handleOpenFile} variant="outline" className="gap-2">
          <FolderOpen className="h-4 w-4" />
          Open File
        </Button>
        <Button onClick={handleValidateFile} variant="secondary" className="gap-2">
          <CheckCircle className="h-4 w-4" />
          Validate File
        </Button>
                <Button onClick={handleGenerateJSON} variant="secondary" className="gap-2">
          <CheckCircle className="h-4 w-4" />
          Generate JSON
        </Button>
      </div>

      {/* Scrollable Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-[30%]">System</TableHead>
                <TableHead className="w-[30%]">user</TableHead>
                <TableHead className="w-[30%]">assistant</TableHead>
                <TableHead className="w-[10%]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trainingData.map((row) => (
                <TableRow key={row.rowId}>
                  <TextAreaTrainingData messageId={0} row={row} editingId={editingRowId} editValues={editValues} setEditValues={setEditValues} handleStartEdit={handleStartEdit} />
                  <TextAreaTrainingData messageId={1} row={row} editingId={editingRowId} editValues={editValues} setEditValues={setEditValues} handleStartEdit={handleStartEdit} />
                  <TextAreaTrainingData messageId={2} row={row} editingId={editingRowId} editValues={editValues} setEditValues={setEditValues} handleStartEdit={handleStartEdit} />
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {editingRowId === row.rowId && (
                        <div className="flex gap-1">
                          <Button size="sm" onClick={handleSaveChanges} className="text-xs px-2 py-1 h-7">
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleDiscardChanges} className="text-xs px-2 py-1 h-7">
                            Discard
                          </Button>
                        </div>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-background border border-border z-50">
                          <DropdownMenuItem onClick={() => handleShowRowData(row)}>
                            Show All Row Data
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(row.rowId)}
                            className="text-destructive focus:text-destructive"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Row Data Modal */}
      <Dialog open={showRowDataModal} onOpenChange={setShowRowDataModal}>
        <DialogContent className="border border-primary">
          <DialogHeader>
            <DialogTitle>Row Data</DialogTitle>
          </DialogHeader>
          <div className="bg-muted/50 rounded-lg p-4 overflow-auto max-h-[400px]">
            <pre className="text-sm text-foreground whitespace-pre-wrap">
              {selectedRowData ? JSON.stringify(selectedRowData, null, 2) : ''}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
